import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, getCountFromServer, getDocs, orderBy, limit, startAfter, limitToLast, endBefore } from 'firebase/firestore';
import { db } from '../firebase';
import Table from '../components/Table';
import './Customers.css';

function safeToDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getMonthKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function getCustomerCity(customerData) {
  if (!customerData?.selectedCityName || customerData?.selectedCityId === 'all_cities') return 'N/A';
  return customerData.selectedCityName;
}

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100); // Show 100 customers per page
  const firstDocRef = useRef(null);
  const lastDocRef = useRef(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | neverBooked | bookedInMonth | inactive2Months
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all'); // 01-12 or 'all'

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerActivity, setCustomerActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Global search (across all customers, not just current page)
  const [searchCustomers, setSearchCustomers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRunIdRef = useRef(0);

  const columns = [
    {
      key: 'customerName',
      title: 'Customer Name',
      className: 'customer-name-col',
      render: (value, row) => (
        <button
          type="button"
          className="customer-link"
          onClick={() => handleOpenCustomer(row)}
        >
          {value}
        </button>
      )
    },
    {
      key: 'phoneNumber',
      title: 'Phone Number',
      className: 'phone-col'
    },
    {
      key: 'city',
      title: 'City',
      className: 'city-col'
    },
    {
      key: 'joinedDate',
      title: 'Joined Date',
      render: (value, row) => {
        const date = safeToDate(row.createdAt);
        return date ? date.toLocaleDateString() : '—';
      }
    }
  ];

  const fetchTotalCount = useCallback(async () => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'customers'));
      setTotalCount(snapshot.data().count || 0);
    } catch (err) {
    }
  }, []);

  const fetchCustomers = useCallback(async (direction = 'first') => {
    try {
      setLoading(true);
      setError('');
      
      let customersQuery;
      
      if (direction === 'first') {
        // Fetch first page - newest customers first
        customersQuery = query(
          collection(db, 'customers'),
          orderBy('createdAt', 'desc'),
          limit(pageSize + 1) // Fetch one extra to check if there's a next page
        );
      } else if (direction === 'next' && lastDocRef.current) {
        // Fetch next page
        customersQuery = query(
          collection(db, 'customers'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDocRef.current),
          limit(pageSize + 1)
        );
      } else if (direction === 'prev' && firstDocRef.current) {
        // Fetch previous page
        customersQuery = query(
          collection(db, 'customers'),
          orderBy('createdAt', 'desc'),
          endBefore(firstDocRef.current),
          limitToLast(pageSize + 1)
        );
      } else {
        setLoading(false);
        return;
      }
      
      const customersSnapshot = await getDocs(customersQuery);
      const docs = customersSnapshot.docs;
      
      // Check if there's a next page
      const hasMore = docs.length > pageSize;
      setHasNextPage(hasMore);
      
      // Remove the extra document used for pagination check
      const validDocs = hasMore ? docs.slice(0, pageSize) : docs;
      
      // Store first and last document for pagination
      if (validDocs.length > 0) {
        firstDocRef.current = validDocs[0];
        lastDocRef.current = validDocs[validDocs.length - 1];
      }
      
      const baseCustomers = validDocs.map((customerDoc) => {
        const customerData = customerDoc.data();
        const customerId = customerDoc.id;
        return {
          id: customerId,
          customerId,
          customerName: customerData.profile?.name || 'N/A',
          phoneNumber: customerData.phoneNumber || 'N/A',
          city: getCustomerCity(customerData),
          createdAt: customerData.createdAt,
          profileCompleted: customerData.profileCompleted || false
        };
      });

      // Fetch last booking per customer in parallel (1 query per customer).
      const lastBookings = await Promise.all(
        baseCustomers.map(async (c) => {
          try {
            const q = query(
              collection(db, 'bookings'),
              where('customerId', '==', c.customerId),
              orderBy('createdAt', 'desc'),
              limit(1)
            );
            const snap = await getDocs(q);
            if (snap.empty) {
              return { customerId: c.customerId, hasBooking: false, lastBooking: null };
            }
            const d = snap.docs[0].data();
            return { customerId: c.customerId, hasBooking: true, lastBooking: d.createdAt || d.bookingDate || null };
          } catch (e) {
            return { customerId: c.customerId, hasBooking: false, lastBooking: null };
          }
        })
      );

      const lastMap = new Map(lastBookings.map((x) => [x.customerId, x]));
      const customersData = baseCustomers.map((c) => {
        const lb = lastMap.get(c.customerId);
        return {
          ...c,
          hasBooking: lb?.hasBooking || false,
          lastBooking: lb?.lastBooking || null
        };
      });
      
      setCustomers(customersData);
    } catch (err) {
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    // Initial load only to avoid cursor-driven refetch loops.
    fetchCustomers('first');
    fetchTotalCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllCustomersForSearch = useCallback(async (runId) => {
    // Fetch customers in batches and compute last booking only for matches.
    const BATCH_SIZE = 250;
    const MAX_CUSTOMERS = 5000; // safety cap
    const all = [];
    let cursor = null;

    while (true) {
      if (searchRunIdRef.current !== runId) return [];

      const q = cursor
        ? query(collection(db, 'customers'), orderBy('createdAt', 'desc'), startAfter(cursor), limit(BATCH_SIZE))
        : query(collection(db, 'customers'), orderBy('createdAt', 'desc'), limit(BATCH_SIZE));

      const snap = await getDocs(q);
      if (snap.empty) break;

      for (const docSnap of snap.docs) {
        const d = docSnap.data();
        all.push({
          id: docSnap.id,
          customerId: docSnap.id,
          customerName: d.profile?.name || 'N/A',
          phoneNumber: d.phoneNumber || 'N/A',
          city: getCustomerCity(d),
          createdAt: d.createdAt,
          profileCompleted: d.profileCompleted || false
        });
      }

      cursor = snap.docs[snap.docs.length - 1];
      if (snap.docs.length < BATCH_SIZE) break;
      if (all.length >= MAX_CUSTOMERS) break;
    }

    return all;
  }, []);

  useEffect(() => {
    const q = (searchText || '').trim();
    if (!q) {
      setSearchCustomers([]);
      setSearchLoading(false);
      return;
    }

    const runId = ++searchRunIdRef.current;
    setSearchLoading(true);

    const t = setTimeout(async () => {
      try {
        const all = await fetchAllCustomersForSearch(runId);
        if (searchRunIdRef.current !== runId) return;

        const qLower = q.toLowerCase();
        const qDigits = q.replace(/\D/g, '');

        const matches = all.filter((c) => {
          const name = (c.customerName || '').toLowerCase();
          const phone = String(c.phoneNumber || '');
          const phoneDigits = phone.replace(/\D/g, '');

          if (qDigits.length >= 4 && phoneDigits.includes(qDigits)) return true;
          return name.includes(qLower) || phone.includes(qLower);
        });

        // Enrich only matches with last booking metadata (to support filters).
        const enriched = await Promise.all(
          matches.map(async (c) => {
            try {
              const qLast = query(
                collection(db, 'bookings'),
                where('customerId', '==', c.customerId),
                orderBy('createdAt', 'desc'),
                limit(1)
              );
              const s = await getDocs(qLast);
              if (s.empty) return { ...c, hasBooking: false, lastBooking: null };
              const bd = s.docs[0].data();
              return { ...c, hasBooking: true, lastBooking: bd.createdAt || bd.bookingDate || null };
            } catch (e) {
              return { ...c, hasBooking: false, lastBooking: null };
            }
          })
        );

        if (searchRunIdRef.current !== runId) return;
        setSearchCustomers(enriched);
      } finally {
        if (searchRunIdRef.current === runId) setSearchLoading(false);
      }
    }, 350); // debounce

    return () => clearTimeout(t);
  }, [fetchAllCustomersForSearch, searchText]);

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
      fetchCustomers('next');
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      fetchCustomers('prev');
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchCustomers('first');
    fetchTotalCount();
  };

  const baseListForFilters = useMemo(() => {
    const q = (searchText || '').trim();
    return q ? searchCustomers : customers;
  }, [customers, searchCustomers, searchText]);

  const yearsForSelect = useMemo(() => {
    const years = new Set();
    for (const c of baseListForFilters) {
      const d = safeToDate(c.lastBooking);
      if (d) years.add(d.getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [baseListForFilters]);

  useEffect(() => {
    if (!selectedYear && yearsForSelect.length > 0) {
      setSelectedYear(String(yearsForSelect[0]));
    }
  }, [selectedYear, yearsForSelect]);

  const monthOptions = [
    { key: 'all', label: 'All months' },
    { key: '01', label: 'January' },
    { key: '02', label: 'February' },
    { key: '03', label: 'March' },
    { key: '04', label: 'April' },
    { key: '05', label: 'May' },
    { key: '06', label: 'June' },
    { key: '07', label: 'July' },
    { key: '08', label: 'August' },
    { key: '09', label: 'September' },
    { key: '10', label: 'October' },
    { key: '11', label: 'November' },
    { key: '12', label: 'December' }
  ];

  const filteredCustomers = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    const now = new Date();
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

    const list = baseListForFilters;

    return list.filter((c) => {
      // In global search mode, baseListForFilters is already the search result set.
      // Still keep local match as a fallback for non-search mode.
      if (q && list === customers) {
        const name = (c.customerName || '').toLowerCase();
        const phone = String(c.phoneNumber || '');
        const matchesSearch = name.includes(q) || phone.includes(q);
        if (!matchesSearch) return false;
      }

      if (filterMode === 'neverBooked') {
        return c.hasBooking !== true;
      }

      if (filterMode === 'inactive2Months') {
        // Exclude never booked as requested
        if (c.hasBooking !== true) return false;
        const last = safeToDate(c.lastBooking);
        if (!last) return false;
        return last < twoMonthsAgo;
      }

      if (filterMode === 'bookedInMonth') {
        if (!selectedYear || selectedMonth === 'all') return true;
        const last = safeToDate(c.lastBooking);
        if (!last) return false;
        // Quick prefilter: if last booking isn't in the selected month, we still might have older booking in that month.
        // For page-level UX, we keep it simple and rely on last booking month match.
        return getMonthKey(last) === `${selectedYear}-${selectedMonth}`;
      }

      return true;
    });
  }, [baseListForFilters, customers, filterMode, searchText, selectedMonth, selectedYear]);

  const handleOpenCustomer = async (customerRow) => {
    setSelectedCustomer(customerRow);
    setCustomerActivity([]);
    setActivityLoading(true);
    try {
      const q = query(
        collection(db, 'bookings'),
        where('customerId', '==', customerRow.customerId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const activities = [];
      snap.forEach((docSnap) => {
        const b = docSnap.data();
        const originalPrice = b.originalPrice ?? b.servicePrice ?? b.totalPrice ?? 0;
        const totalPrice = b.totalPrice ?? 0;
        const original = toNumberOrZero(originalPrice);
        const total = toNumberOrZero(totalPrice);
        const discount = Math.max(0, original - total);
        const offerAppliedBool = discount > 0 || (b.offerApplied != null && b.offerApplied !== false);
        const offerPercent = offerAppliedBool && original > 0 ? Math.round((discount / original) * 100) : null;

        activities.push({
          id: docSnap.id,
          bookingReference: b.bookingReference || docSnap.id,
          bookingDate: b.bookingDate,
          bookingTime: b.bookingTime,
          bookingStatus: b.bookingStatus,
          salonName: b.salonName,
          salonId: b.salonId,
          serviceName: b.serviceName,
          originalPrice: original,
          totalPrice: total,
          offerAppliedBool,
          offerPercent
        });
      });
      setCustomerActivity(activities);
    } catch (e) {
      setCustomerActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleCloseCustomer = () => {
    setSelectedCustomer(null);
    setCustomerActivity([]);
    setActivityLoading(false);
  };

  if (error) {
    return (
      <div className="customers-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customers-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Customers Management</h2>
          <p>Total: {totalCount} registered customers | Showing: {customers.length} per page (Newest First)</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="customers-controls">
        <div className="customers-search">
          <input
            className="customers-search-input"
            placeholder="Search by name or phone number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchLoading && <div className="customers-search-hint">Searching…</div>}
        </div>

        <div className="customers-filters">
          <label className="customers-filter-field">
            <span className="customers-filter-label">Filter</span>
            <select
              className="customers-filter-select"
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              disabled={loading}
            >
              <option value="all">All customers</option>
              <option value="neverBooked">Never booked</option>
              <option value="bookedInMonth">Booked (month-wise)</option>
              <option value="inactive2Months">No booking in past 2 months</option>
            </select>
          </label>

          <label className="customers-filter-field">
            <span className="customers-filter-label">Year</span>
            <select
              className="customers-filter-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={loading || filterMode !== 'bookedInMonth' || yearsForSelect.length === 0}
            >
              {yearsForSelect.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className="customers-filter-field">
            <span className="customers-filter-label">Month</span>
            <select
              className="customers-filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={loading || filterMode !== 'bookedInMonth'}
            >
              {monthOptions.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          <button
            className="customers-filter-reset-btn"
            onClick={() => {
              setSearchText('');
              setFilterMode('all');
              setSelectedMonth('all');
            }}
            disabled={loading}
            type="button"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="table-section">
        <Table
          columns={columns}
          data={filteredCustomers}
          loading={loading}
          emptyMessage="No customers found"
        />
      </div>

      {/* Pagination Controls */}
      {(searchText || '').trim() ? (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {filteredCustomers.length} matched customers
          </div>
          <div className="pagination-controls">
            <span className="page-number">Search results</span>
          </div>
        </div>
      ) : (
        <div className="pagination-container">
          <div className="pagination-info">
            Page {currentPage} | Showing {filteredCustomers.length} customers
          </div>
          <div className="pagination-controls">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1 || loading}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="page-number">Page {currentPage}</span>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Customer Activity Modal */}
      {selectedCustomer && (
        <div className="modal-overlay" onClick={handleCloseCustomer}>
          <div className="modal-content customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h3>{selectedCustomer.customerName}</h3>
                <p className="modal-subtitle">{selectedCustomer.phoneNumber}</p>
              </div>
              <button className="modal-close-btn" onClick={handleCloseCustomer} type="button">
                ✕
              </button>
            </div>

            <div className="modal-body">
              {activityLoading ? (
                <div className="activity-loading">Loading activity...</div>
              ) : customerActivity.length === 0 ? (
                <div className="activity-empty">No booking activity found.</div>
              ) : (
                <div className="activity-list">
                  {customerActivity.map((a) => {
                    const d = safeToDate(a.bookingDate);
                    const dateLabel = d ? d.toLocaleDateString() : '—';
                    return (
                      <div key={a.id} className="activity-item">
                        <div className="activity-top">
                          <div className="activity-left">
                            <div className="activity-ref">{a.bookingReference}</div>
                            <div className="activity-meta">
                              <span>{dateLabel}</span>
                              <span className="activity-sep">·</span>
                              <span>{a.bookingTime || '—'}</span>
                              <span className="activity-sep">·</span>
                              <span>{a.salonName || '—'}</span>
                            </div>
                            <div className="activity-service">{a.serviceName || '—'}</div>
                          </div>
                          <div className="activity-right">
                            <span className={`status-badge status-${String(a.bookingStatus || '').toLowerCase()}`}>
                              {a.bookingStatus || '—'}
                            </span>
                          </div>
                        </div>

                        <div className="activity-bottom">
                          <div className="activity-price">
                            <span className="activity-price-label">Cost:</span>
                            <span className="activity-price-value">₹{toNumberOrZero(a.totalPrice).toFixed(0)}</span>
                          </div>
                          <div className="activity-offer">
                            <span className="activity-offer-label">Offer:</span>
                            <span className="activity-offer-value">
                              {a.offerAppliedBool ? `Yes${a.offerPercent != null ? ` (${a.offerPercent}%)` : ''}` : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;













